import { Button, Card, Flex, Spin, Table, TableColumnsType } from "antd";
import { TNotification } from "api/services/models/notification.types";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { getNotifications } from "api/services/NotificationService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import dayjs from "dayjs";
import { ReactComponent as AddNotificatoinIcon } from "Icons/vuesax/addsquare.svg";
import { useEffect, useState } from "react";
import NewNotification from "./NewNotification";

const columns: TableColumnsType<TNotification> = [
  {
    key: "fromUserId",
    dataIndex: "fromUserId",
    title: "From"
  },
  {
    key: "to",
    dataIndex: "toUserId",
    width: "244px",
    minWidth: 244,
    title: "To",
    render: (_, r) => `${r.toUserId}@${r.toAccountId}`
  },
  {
    key: "messageText",
    dataIndex: "messageText",
    title: "Message"
  },
  {
    key: "createdDateTime",
    dataIndex: "createdDateTime",
    title: "Sent At",
    defaultSortOrder: "descend",
    render: datetime => dayjs(datetime).format("MM-DD-YY hh:mm:ss"),
    sorter: (a, b) => (dayjs(a.createdDateTime).isBefore(dayjs(b.createdDateTime)) ? -1 : 1)
  },
  {
    key: "isSent",
    dataIndex: "isSent",
    title: "Is Sent",
    filters: [
      {
        text: "Sent",
        value: "sent"
      },
      {
        text: "Unsent",
        value: "unsent"
      }
    ],
    render: v => (v ? "Yes" : "No"),
    onFilter: (value, n) => {
      console.log(value);
      return value === "sent" ? n.isSent : !n.isSent;
    }
  },
  {
    key: "errorMessage",
    dataIndex: "errorMessage",
    title: "Status"
  }
];

const NotificationCenter = () => {
  const { webDUser, accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [showNewNotificationDialog, setShowNewNotificationDialog] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    if (accountId && webDUser?.user.userId) {
      setLoading(true);
      setError(null);
      getNotifications()
        .then(setNotifications)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, []);

  const onAddNotification = (newNotifications?: TNotification[]) => {
    setShowNewNotificationDialog(false);
    if (newNotifications?.length) {
      setNotifications(ns => [...ns, ...newNotifications]);
      setTableKey(k => k + 1);
    }
  };

  return (
    <Card styles={{ body: { padding: "4px" } }}>
      <Flex justify="space-between" style={{ margin: "8px 0" }}>
        <Button
          type="primary"
          className="default-button primary-btn"
          disabled={!webDUser?.hasPrivilege(WebDPrivilege.CreateNewMessage)}
          icon={<AddNotificatoinIcon className="default-icon" />}
          iconPosition={"start"}
          onClick={() => setShowNewNotificationDialog(true)}
          title="New Notification">
          New Message
        </Button>
      </Flex>
      {loading && <Spin tip="Loading, please wait..." />}
      {error && <ErrorViewer error={error} />}
      <Flex gap={16}>
        <Table<TNotification>
          key={tableKey}
          dataSource={notifications}
          columns={columns}
          rowKey={"messageId"}
          pagination={{
            hideOnSinglePage: true,
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 30, 40, 50]
          }}
          style={{ border: "none" }}
          className="table-style"
          components={{
            body: {
              cell: (props: any) => (
                <td
                  {...props}
                  style={{
                    borderBottomWidth: "1px",
                    borderBottomColor: "#f0f0f0",
                    borderBottomStyle: "solid"
                  }}
                />
              )
            }
          }}/>
      </Flex>
      {showNewNotificationDialog && <NewNotification onClose={onAddNotification} />}
    </Card>
  );
};

export default NotificationCenter;
